import type { FetchResult } from '@apollo/client';
import { Cancel, Check } from '@mui/icons-material';
import Edit from '@mui/icons-material/Edit';
import {
  Box,
  ClickAwayListener,
  InputAdornment,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useRef, useState, type ElementType } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';

const Root = styled('div')(({ theme }) => ({
  color: theme.palette.common.black,
  backgroundColor: 'transparent',
}));

const LabelContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
});

const ActionIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.dark,
  padding: theme.spacing(0.5),
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

export interface InlineEditTextFieldProps {
  inputId: string;
  value: string;
  update: (value: string) => Promise<FetchResult | undefined> | void;
  tooltip: string;
  defaultEdit?: boolean;
  validationSchema?: object /* A Yup schema or a function that returns a Yup schema */;
  className?: string;
  /** The element the read view renders the value as, e.g. a heading for a page title. */
  labelComponent?: ElementType;
}

export const InlineEditTextField = ({
  inputId,
  value,
  update,
  tooltip,
  defaultEdit,
  validationSchema = {},
  className,
  labelComponent,
}: InlineEditTextFieldProps) => {
  const { t } = useTranslation();

  const [editing, setEditing] = useState(defaultEdit ?? false);

  // The edit button unmounts while editing, so leaving edit mode from inside the field would
  // drop focus to the page. A click-away leaves focus wherever the user clicked.
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const finishEditing = () => {
    // A click-away can close the field while a save is in flight; focus stays where the user put it.
    if (!formRef.current) return;
    flushSync(() => setEditing(false));
    editButtonRef.current?.focus();
  };

  const formik = useFormik({
    initialValues: { name: value },
    onSubmit: async (values: { name: string }) => {
      const response = await update(values.name);
      if (response && response.errors) formik.resetForm({ values: { name: value } });
      finishEditing();
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
  });

  const clickAway = () => {
    setEditing(false);
    formik.resetForm({ values: { name: value } });
  };

  // Called when editing is aborted from inside the field: escape or the cancel button
  const cancel = () => {
    finishEditing();
    formik.resetForm({ values: { name: value } });
  };

  // Called on every key press to figure out if escape was pressed
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') cancel();
  };

  // The read view stays inline: a component declared in here would remount on every render and
  // take the focused edit button with it.
  return (
    <Root className={className}>
      {!editing ? (
        <LabelContainer>
          <Typography
            component={labelComponent ?? 'p'}
            onClick={() => setEditing(true)}
            sx={{ marginRight: '8px' }}
          >
            {formik.values.name}
          </Typography>
          <ActionIconButton
            label={tooltip}
            onClick={() => setEditing(true)}
            ref={editButtonRef}
            size="large"
          >
            <Edit />
          </ActionIconButton>
        </LabelContainer>
      ) : (
        <ClickAwayListener onClickAway={clickAway}>
          <form onSubmit={formik.handleSubmit} ref={formRef}>
            <TextField
              id={inputId}
              name="name"
              variant="standard"
              autoFocus
              fullWidth
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
              onKeyDown={onKeyDown}
              InputProps={{
                autoFocus: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      color="secondary"
                      label={t('common.actions.accept')}
                      onClick={() => formik.handleSubmit()}
                      size="medium"
                    >
                      <Check fontSize="inherit" />
                    </IconButton>

                    <IconButton
                      color="secondary"
                      label={t('common.actions.cancel')}
                      onClick={() => cancel()}
                      size="medium"
                    >
                      <Cancel fontSize="inherit" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </ClickAwayListener>
      )}
    </Root>
  );
};
